import _ from 'underscore';
import { CampaignuiBaseColumn } from '@bingads-webui-campaign/campaignui-base-column';
import { getViolationsFromCategoryFAIDMapping, shouldRepairGoogleImportVideoAd } from '@bingads-webui-campaign/delivery-status-popup-factory';
import template from './delivery.pug';

export class DeliveryColumn extends CampaignuiBaseColumn {
  constructor({
    i18n,
    isForVNext,
    isCampaign,
    property,
    scope,
    permissions,
    view,
  }) {
    const isForCampaignPage = scope && scope.location && (scope.location.pathname === '/campaign/vnext/campaigns');
    const newHelpIdForMMAInDSA = permissions && permissions.IsMMAInDSAEnabled && isForCampaignPage;

    const isForAdsPage = scope && scope.location && (scope.location.pathname === '/campaign/vnext/ads');
    const isEditorialCategoryEnabled = permissions && permissions.IsEditorialCategoryEnabled;

    if (_.isUndefined(property)) {
      if (isCampaign) {
        // eslint-disable-next-line no-param-reassign
        property = ['Id', 'Name', 'DeliveryStatus', 'DeliveryStatusDetails', 'Budget'];
      } else if (isEditorialCategoryEnabled && isForAdsPage) {
        // EditorialCategories pilot
        // eslint-disable-next-line no-param-reassign
        property = ['DeliveryStatus', 'DeliveryStatusDetails', 'EditorialCategories'];
      } else {
        // eslint-disable-next-line no-param-reassign
        property = ['DeliveryStatus', 'DeliveryStatusDetails'];
      }

      if (isCampaign) {
        property.push('AssociatedExperiment');
      }
    }

    const title = i18n.getString('ColumnName_DeliveryStatus');

    super({
      name: 'Delivery',
      property,
      title,
      sortable: 'DeliveryStatus',
      type: 'string',
      helpId: newHelpIdForMMAInDSA ? 'pop_BA_CampaignGrids_Delivery' : 'Page_CampaignGrids_Delivery',
      bodyClasses: ['grid-column-Delivery'],
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: title }),
    });

    this.i18n = i18n;
    this.isForVNext = isForVNext;
    this.isCampaign = isCampaign;
    this.isEditorialCategoryEnabled = isEditorialCategoryEnabled;
    this.isForAdsPage = isForAdsPage;
    this.instrumentation = view && view.instrumentation;
    this.IsGoogleImportVideoAdsEnabled = permissions && permissions.IsGoogleImportVideoAdsEnabled;
    this.showBudgetSuggestion = permissions && permissions.dynamic && permissions.dynamic.EnableBudgetSuggestion;
  }

  pickProperty(item) {
    const videoNeedsToBeConverted = this.IsGoogleImportVideoAdsEnabled && shouldRepairGoogleImportVideoAd(item);

    if (this.isCampaign) {
      return {
        Id: item.Id,
        DeliveryStatus: item.DeliveryStatus,
        Budget: item.Budget,
        IsAppealable: item.IsAppealable,
        AssociatedExperiment: item.AssociatedExperiment,
        ExperimentId: item.ExperimentId,
        DeliveryStatusDetails: item.DeliveryStatusDetails,
      };
    } else if (this.isEditorialCategoryEnabled && this.isForAdsPage) {
      // EditorialCategories pilot
      return {
        Id: item.Id,
        DeliveryStatus: item.DeliveryStatus,
        EditorialCategories: item.EditorialCategories,
        VideoNeedsToBeConverted: videoNeedsToBeConverted,
        DeliveryStatusDetails: item.DeliveryStatusDetails,
      };
    }
    return {
      Id: item.Id,
      DeliveryStatus: item.DeliveryStatus,
      DeliveryStatusDetails: item.DeliveryStatusDetails,
    };
  }

  template({ value }) {
    let { DeliveryStatus } = value;
    const { DeliveryStatusDetails } = value;
    let showEllipsisForExperiment = false;

    if (this.isCampaign
      && value.ExperimentId
      && value.AssociatedExperiment) {
      showEllipsisForExperiment = true;
      DeliveryStatus = _.result({
        Promoted: 'ExperimentApplied',
        Graduated: 'ExperimentApplied',
        Ended: 'ExperimentEnded',
      }, value.AssociatedExperiment.Status, DeliveryStatus);
    }

    const errorStatusList = [
      'AdGroupExpired',
      'AudienceTooNarrow',
      'CampaignExpired',
      'CreditCardExpired',
      'IOExceeded',
      'PaymentInformationMissing',
      'InvalidFinancialStatusOther',
      'MigrationPaused',
      'CampaignDeleted',
      'CampaignBudgetPaused',
      'AdGroupDeleted',
      'CamAssetRejected',
      'EditorialRejected',
      'EditorialAppealRejected',
      'CPSNonRetailDomain',
      'LinkedInAccountServingHold',
      'LinkedInCompanyPagePaidMediaRequired',
      'DisclaimerInfoRejected',
      'DisclaimerInfoRejectedAppealRejected',
    ];
    // these are not treated as warning or error
    const whiteList = [
      'Eligible',
      'EditorialApprovedLimited',
      'EditorialApprovedLimitedAppealRejected',
      'Incomplete',
      'InlineOpportunity',
      'AdGroupAudienceAssociationPaused',
      'AdGroupAudienceAssociationExcluded',
      'ExperimentApplied',
      'ExperimentEnded',
      'AutoBiddingLimitedUnAttainableTarget',
      'CampaignInReview',
      'DisclaimerInfoApprovedLimited',
      'DisclaimerInfoApprovedLimitedAppealRejected',
    ];
    const inWhiteList = _.contains(whiteList, DeliveryStatus);
    const isErrorStatus = _.contains(errorStatusList, DeliveryStatus);
    const isWarningStatus = !inWhiteList && !isErrorStatus;

    const hasRejectReasonWhitelist = [
      'Eligible',
      'BudgetSuggestions',
      'Incomplete',
      'GeoCodingIncomplete',
      'CampaignBudgetPaused',
      'InlineOpportunity',
    ];

    const activity = this.instrumentation && this.instrumentation.autobiddingLimitedStatus &&
      this.instrumentation.autobiddingLimitedStatus.create().renderDeliveryStatus.create();

    if (activity && this.isCampaign && _.contains(['AutoBiddingLimitedInventoryLimit', 'AutoBiddingLimitedMaxCPCRestriction', 'AutoBiddingLimitedBudgetRestriction', 'AutoBiddingLimitedUnAttainableTarget', 'AutoBiddingPortfolioBidStrategyTrainingStatus'], DeliveryStatus)) {
      activity.trace(JSON.stringify({
        campaignId: value.Id,
        deliveryStatus: DeliveryStatus,
      }));
    }

    const adsActivity = this.instrumentation && this.instrumentation.logDeliveryStatus && this.instrumentation.logDeliveryStatus.create().logAdsDeliveryStatus.create();
    if (adsActivity && _.contains(['EditorialApprovedLimitedAppealRejected', 'EditorialAppealRejected'], DeliveryStatus)) {
      adsActivity.trace(JSON.stringify({
        adId: value.Id,
        deliveryStatus: DeliveryStatus,
      }));
    }

    // const showRejectPopup = !_.contains(hasRejectReasonWhitelist, DeliveryStatus);
    const showBudgetSuggestionWithDeliveryStatusDetails = this.showBudgetSuggestion && _.includes(DeliveryStatusDetails, 'BudgetSuggestions');
    const showRejectPopup = !_.contains(hasRejectReasonWhitelist, DeliveryStatus) && !showBudgetSuggestionWithDeliveryStatusDetails;

    // popup for 0) pilot and only for ads-page; 1) reject; or 2) mapped EditorialCategory is not empty
    let shouldEditorialCategoriesPopup = false;
    if (this.isEditorialCategoryEnabled && this.isForAdsPage) {
      const { EditorialCategories } = value;
      shouldEditorialCategoriesPopup = getViolationsFromCategoryFAIDMapping(EditorialCategories).length > 0;
    }

    // const showBudgetLandscape = (DeliveryStatus === 'BudgetSuggestions' || DeliveryStatus === 'CampaignBudgetPaused') && !!value.Budget;
    const showBudgetLandscape = (DeliveryStatus === 'BudgetSuggestions' || DeliveryStatus === 'CampaignBudgetPaused' || showBudgetSuggestionWithDeliveryStatusDetails) && !!value.Budget;
    const budgetLandscapeClassName = this.isCampaign
      ? 'iconba iconba-Financial bidlandscape_icon gridDetailsImage spritedimagealign clickable delivery_status_with_budget_suggestions'
      : ' status_icon gridDetailsImage spritedimagealign clickable delivery_status_with_budget_suggestions';

    const isCampaignBudgetLandScape = this.isCampaign && showBudgetLandscape;
    const containerClassName = isCampaignBudgetLandScape
      ? 'layout-marked-element status-row budget-landscape-row grid-editable-cell'
      : 'layout-marked-element status-row';

    const opportunityPopupHandlerClass = 'delivery_status_with_inline_opportunities';
    const inlineOpportunityClassname = `iconba iconba-CustomerInsightsShape inline-recommendation-grid-icon clickable ${opportunityPopupHandlerClass}`;
    const vnextInlineOpportunityClassname = 'iconba iconba-CustomerInsightsShape inline-recommendation-grid-icon clickable';

    // seems not in use, so not update this
    const withInlineAppeals = this.isCampaign
      && (DeliveryStatus === 'EditorialRejected' || DeliveryStatus === 'EditorialApprovedLimited') && value.IsAppealable === true;
    const showInlineOpportunityPopup = DeliveryStatus === 'InlineOpportunity';
    const shouldShowPopUpStyleInVnext = this.isForVNext
      && (showRejectPopup || showBudgetLandscape || showInlineOpportunityPopup);

    const vnextContainerClasses = [];
    if (isCampaignBudgetLandScape) {
      vnextContainerClasses.push('budget-landscape-row clickable');
    }
    if (showInlineOpportunityPopup) {
      vnextContainerClasses.push(opportunityPopupHandlerClass);
      vnextContainerClasses.push('clickable');
    }
    const vNextContainerClassName = vnextContainerClasses.join(' ');

    return template({
      Id: value.Id,
      isErrorStatus,
      isWarningStatus,
      showRejectPopup: showRejectPopup || shouldEditorialCategoriesPopup,
      showInlineOpportunityPopup,
      inlineOpportunityClassname,
      vnextInlineOpportunityClassname,
      DeliveryStatus,
      i18n: this.i18n,
      isForVNext: this.isForVNext,
      showBudgetLandscape,
      budgetLandscapeClassName,
      withInlineAppeals,
      showEllipsisForExperiment,
      containerClassName,
      vNextContainerClassName,
      shouldShowPopUpStyleInVnext,
      shouldShowSensitiveContentWarning: shouldEditorialCategoriesPopup,
      videoNeedsToBeConverted: value.VideoNeedsToBeConverted,
    });
  }
}
